import { useCallback, useEffect, useState } from "react";

import Box from "@/components/Box";
import Divider from "@/components/Divider";
import { ExternalTextLink } from "@/components/Links";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import {
  PATIENT_NOTIFICATION_ALL,
  PATIENT_NOTIFICATION_EXCLUDE_RESULTS,
  PATIENT_NOTIFICATION_NONE,
  REVIEW_STATUS_FOR_PRACTITIONER_REVIEW,
  REVIEW_STATUS_FOR_REVIEW
} from "@/core/constants";
import { CMP_BUSINESS_TAB_URL, getCmpUrl } from "@/core/urls";
import Badge from "@/tpo/Badge";
import { PanelBoxV2 } from "@/tpo/Boxes";
import Checkbox from "@/tpo/Checkbox";
import Group from "@/tpo/Group";
import { Error } from "@/tpo/InputWrapper";
import LinkWrapper from "@/tpo/LinkWrapper";
import ButtonV2 from "@/v2/Buttons";
import { useMutation } from "@apollo/client";

import { PATIENT_TESTS, RELEASE_USER_TEST_MUTATION } from "./graphql";

function ReleaseResultsTemplate({
  title = "Release results to patient",
  description = `After reviewing your clients results, make them available for viewing on their patient account. We will send them an email notification to let them know their results are ready to be viewed.`,
  children
}) {
  return (
    <PanelBoxV2 maxWidth={1020} stacked gap={20}>
      <PanelBoxV2
        maxWidth={760}
        outer={{
          pt: [30, 30, 60],
          pb: [30, 30, 80],
          bg: "white",
          px: [20]
        }}
        stacked
        gap={40}
      >
        <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
          {title}
        </Box>
        <Box mt={-40 + 28} fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
          {description}
        </Box>
        {children}
      </PanelBoxV2>
    </PanelBoxV2>
  );
}

function UpdateSettings({ to, notificationSetting, notificationHelpText }) {
  return (
    <Box>
      <Group gap={8}>
        <Box as="span" fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
          Patient notification setting:
        </Box>
        <Badge size={["xs"]} color="white" bg="dark">
          {notificationSetting}
        </Badge>
      </Group>
      <Box fontFamily="gilroyMedium" fontSize={14} mt="8px">
        {notificationHelpText}
      </Box>
      <ButtonV2
        size="xs"
        withChevron
        color="dark"
        sx={{
          fontSize: 10,
          lineSpacing: "1.5px",
          mt: 16
        }}
        as={LinkWrapper}
        to={to}
        underline={false}
      >
        update settings
      </ButtonV2>
    </Box>
  );
}

export default function ReleaseResultsToClient({ userTest }) {
  const { organisation, organisationId } = useOrganisationContext();

  const userTestRequiresAction =
    userTest?.reviewStatus == REVIEW_STATUS_FOR_REVIEW ||
    userTest?.reviewStatus == REVIEW_STATUS_FOR_PRACTITIONER_REVIEW;

  const [released, setReleased] = useState(false);

  const [releaseUserTestMutation] = useMutation(RELEASE_USER_TEST_MUTATION, {
    refetchQueries: [
      {
        query: PATIENT_TESTS,
        variables: {
          allTests: true,
          userId: userTest.user.id,
          userTestId: userTest.id,
          includeUnapproved: true,
          organisationId
        }
      }
    ]
  });
  const [error, setError] = useState();
  const [manuallyReleased, setManuallyReleased] = useState(false);

  const release = useCallback(
    sendNotification => {
      releaseUserTestMutation({
        variables: {
          userTestId: userTest.id,
          sendNotification,
          organisationId: organisationId
        }
      })
        .then(data => {
          if (data?.data?.releaseUserTest?.success) {
            setReleased(true);
          } else {
            setError(
              "Failed to release the user's test. Please contact info@regeneruslabs.com if this issue persists."
            );
          }
        })
        .catch(error => {
          console.error("Error encountered releasing user test", error);
          setError(
            "Failed to release the user's test. Please contact info@regeneruslabs.com if this issue persists."
          );
        });
    },
    [releaseUserTestMutation, userTest.id, organisationId]
  );

  useEffect(() => {
    if (manuallyReleased) {
      setTimeout(() => release(false), 500);
    }
  }, [manuallyReleased, release]);

  const settingsUrl = getCmpUrl(CMP_BUSINESS_TAB_URL, {
    organisationId: organisation.hashId,
    tab: "settings"
  });

  if (organisation?.patientNotifications == PATIENT_NOTIFICATION_ALL) {
    if (released) {
      return (
        <ReleaseResultsTemplate
          title="Results released to patient"
          description="Thank you. Your patient’s results are now available to view in their account. They’ll receive an email notification shortly."
        />
      );
    }

    if (userTestRequiresAction) {
      return (
        <ReleaseResultsTemplate>
          <Group>
            <ButtonV2
              color="green"
              witChevron
              onClick={() => release(true)}
              size={["sm", "sm", "md"]}
            >
              Release to patient
            </ButtonV2>
          </Group>
          {!!error && <Error error={error} />}
          <Divider />
          <UpdateSettings
            to={settingsUrl}
            notificationSetting="opt in all"
            notificationHelpText="Your patient will get email alerts for new test orders and when results are available."
          />
        </ReleaseResultsTemplate>
      );
    }
  }

  const manualReleaseAcknowledgement = (
    <ReleaseResultsTemplate
      title="Results manually released to patient"
      description="Thank you. You have opted to manually release these results to your patient. These results have also been cleared from your results awaiting review."
    />
  );

  if (organisation?.patientNotifications == PATIENT_NOTIFICATION_EXCLUDE_RESULTS) {
    if (userTestRequiresAction) {
      return (
        <ReleaseResultsTemplate
          title="Release results to patient"
          description="Once you've reviewed your client's results, update the order status to show that the results have been shared with your patient. This will remove these results from your review list."
        >
          <Checkbox
            label="I’ve manually released these results to my patient"
            checked={manuallyReleased}
            onChange={({ target: { checked } }) => setManuallyReleased(checked)}
          />
          {!!error && <Error error={error} />}
          <Divider />
          <UpdateSettings
            to={settingsUrl}
            notificationSetting="opt out results"
            notificationHelpText="Your patient will receive notifications only for new orders and will not be contacted when results are available."
          />
        </ReleaseResultsTemplate>
      );
    }

    if (released) {
      return manualReleaseAcknowledgement;
    }
  }

  if (organisation?.patientNotifications == PATIENT_NOTIFICATION_NONE) {
    if (userTestRequiresAction) {
      return (
        <ReleaseResultsTemplate
          title="Release results to patient"
          description="Once you've reviewed your client's results, update the order status to show that the results have been shared with your patient. This will remove these results from your review list."
        >
          <Checkbox
            label="I’ve manually released these results to my patient"
            checked={manuallyReleased}
            onChange={({ target: { checked } }) => setManuallyReleased(checked)}
          />
          {!!error && <Error error={error} />}
          <Divider />
          <UpdateSettings
            to={settingsUrl}
            notificationSetting="opt out all"
            notificationHelpText="No email communications will be sent to your patients"
          />
        </ReleaseResultsTemplate>
      );
    }

    if (released) {
      return manualReleaseAcknowledgement;
    }
  }

  return null;
}
